<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-image-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">Add your logo</h2>
        <p class="desc">
          Upload a clear logo to personalize your storefront and branding.
        </p>
      </div>
    </div>

    <div class="upload-wrap">
      <div class="form-field">
        <div class="label">
          <v-icon class="label-icon" size="16" color="#6b7280">mdi-image-outline</v-icon>
          <span>Business logo</span>
        </div>
      </div>
      
      <input
        ref="logoInput"
        type="file"
        class="file-input"
        accept="image/*"
        @change="onChange"
      />

      <div class="upload-card">
        <div v-if="previewUrl" class="preview">
          <img :src="previewUrl" alt="Logo preview" />
        </div>
        <div v-else class="placeholder">
          <v-icon size="22">mdi-image-outline</v-icon>
          <div class="placeholder-text">No logo selected</div>
        </div>

        <div class="actions">
          <v-btn
            variant="outlined"
            prepend-icon="mdi-upload"
            @click="pickUpload"
          >
            {{ logoFile ? "Change logo" : "Select logo" }}
          </v-btn>
          <v-btn
            v-if="logoFile"
            variant="text"
            color="error"
            prepend-icon="mdi-delete-outline"
            @click="clearLogo"
          >
            Remove
          </v-btn>
        </div>

        <div class="file-name">
          {{ displayFileName }}
        </div>

        <div v-if="logoError" class="api-error">{{ logoError }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, string> | null;
  profileInfo?: any;
}>();

const logoInput = ref<HTMLInputElement | null>(null);
const logoFile = ref<File | null>(null);
const previewUrl = ref<string | null>(null);
const isBlobPreview = ref(false);

const logoError = ref("");
const existingLogoUrl = computed<string | null>(() => props.profileInfo?.logo?.url || null);
const existingLogoName = computed(() => {
  const url = existingLogoUrl.value || "";
  if (!url) return "";
  const raw = url.split("?")[0];
  const last = raw.split("/").pop() || "";
  return last || "Current logo";
});
const displayFileName = computed(() => {
  if (logoFile.value?.name) return logoFile.value.name;
  if (existingLogoUrl.value) return existingLogoName.value;
  return "No file selected";
});

function getValues() {
  return {
    logoFile: logoFile.value,
    previewUrl: previewUrl.value,
  };
}

defineExpose({ getValues });

function revokePreview() {
  if (previewUrl.value && isBlobPreview.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  previewUrl.value = null;
  isBlobPreview.value = false;
}

watch(logoFile, (file) => {
  revokePreview();
  if (file) {
    previewUrl.value = URL.createObjectURL(file);
    isBlobPreview.value = true;
  } else if (existingLogoUrl.value) {
    previewUrl.value = existingLogoUrl.value;
    isBlobPreview.value = false;
  }
});

onBeforeUnmount(() => {
  revokePreview();
});

watch(
  existingLogoUrl,
  (url) => {
    if (!url) return;
    if (logoFile.value) return;
    // Only prefill if user hasn't selected a new logo yet.
    previewUrl.value = url;
    isBlobPreview.value = false;
  },
  { immediate: true },
);

watch(() => props.errors, (errs: any) => {
  logoError.value = "";
  if (!errs) return;
  if (errs.logo) logoError.value = errs.logo;
  if (errs.logoFile) logoError.value = errs.logoFile;
}, { immediate: true });

function pickUpload() {
  logoInput.value?.click();
}

function clearLogo() {
  logoFile.value = null;
  if (logoInput.value) logoInput.value.value = "";
  if (existingLogoUrl.value) {
    previewUrl.value = existingLogoUrl.value;
    isBlobPreview.value = false;
  }
}

function onChange(event: Event) {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0] || null;
  logoFile.value = file;
  if (input) input.value = "";
}
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.upload-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.label-icon {
  flex-shrink: 0;
}

.file-input {
  display: none;
}

.upload-card {
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview {
  width: 100%;
  height: 220px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #edf0f5;
  background: #f8fafc;
  display: grid;
  place-items: center;
}

.preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  max-height: 220px;
  padding: 20px 0;
}

.placeholder {
  height: 220px;
  border-radius: 12px;
  border: 1px dashed #cbd5f5;
  background: #f8f9ff;
  display: grid;
  place-items: center;
  gap: 8px;
  color: #667085;
}

.placeholder-text {
  font-size: 13px;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.file-name {
  color: #5b677a;
  font-size: 13px;
}

.api-error {
  color: #dc2626; /* red-600 */
  font-size: 13px;
  margin-top: 8px;
}
</style>
